import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import {
  Agreement01Icon,
  Building03Icon,
  Calendar03Icon,
  CallIcon,
  CharityIcon,
  FavouriteIcon,
  FirstAidKitIcon,
  GiftIcon,
  HeartAddIcon,
  HelpCircleIcon,
  Home01Icon,
  MapsLocation01Icon,
  Message01Icon,
  Plant01Icon,
  StarIcon,
  Sun03Icon,
  UserGroupIcon,
  UserMultipleIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { type FC } from "react";

export const OPENING_HUB_ICON_KEYS = [
  "handshake",
  "opportunities",
  "addRequest",
  "contactPhone",
  "heart",
  "home",
  "location",
  "sun",
  "chat",
  "calendar",
  "star",
  "building",
  "gift",
  "plant",
  "help",
  "medical",
  "users",
  "charity",
] as const;

export type OpeningHubIconKey = (typeof OPENING_HUB_ICON_KEYS)[number];

export const OPENING_HUB_ACCENT_PRESETS = [
  // Seeded / legacy (keep first for default)
  "#00b4d8",
  "#1d4ed8",
  "#0f766e",
  "#c45c26",
  "#be123c",
  "#7c3aed",
  "#b45309",
  "#365314",
  "#0369a1",
  "#9a3412",
  // Bright blues & cyan
  "#0ea5e9",
  "#06b6d4",
  "#22d3ee",
  "#38bdf8",
  "#3b82f6",
  "#2563eb",
  "#4f46e5",
  "#6366f1",
  "#818cf8",
  // Fresh greens & teal
  "#14b8a6",
  "#2dd4bf",
  "#10b981",
  "#34d399",
  "#22c55e",
  "#4ade80",
  "#84cc16",
  "#a3e635",
  "#65a30d",
  // Hot yellows & oranges
  "#eab308",
  "#facc15",
  "#f59e0b",
  "#fbbf24",
  "#f97316",
  "#fb923c",
  "#ea580c",
  "#fdba74",
  // Punchy reds & rose
  "#ef4444",
  "#f87171",
  "#f43f5e",
  "#fb7185",
  "#e11d48",
  "#dc2626",
  // Pinks & magenta
  "#ec4899",
  "#f472b6",
  "#db2777",
  "#e879f9",
  "#d946ef",
  "#c026d3",
  // Vivid purples
  "#a855f7",
  "#c084fc",
  "#8b5cf6",
  "#a78bfa",
  "#9333ea",
  "#6d28d9",
  // Deep accents (still saturated)
  "#1e3a8a",
  "#134e4a",
  "#7f1d1d",
  "#4c1d95",
  "#831843",
  "#14532d",
  "#0f172a",
] as const;

export const OPENING_HUB_DEFAULT_ACCENT_COLOR =
  OPENING_HUB_ACCENT_PRESETS[0];

const ACCENT_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

export type OpeningHubOptionFormItem = {
  id: string;
  label: string;
  description: string;
  path: string;
  iconKey: OpeningHubIconKey;
  accentColor: string;
  enabled: boolean;
};

export const MAX_OPENING_HUB_OPTIONS = 12;

/** Keep in sync with server assertSafeInternalPath. */
const SAFE_INTERNAL_PATH_RE = /^\/[a-zA-Z0-9][a-zA-Z0-9/_-]*$/;

const ICON_LABELS: Record<OpeningHubIconKey, string> = {
  handshake: "לחיצת יד",
  opportunities: "התנדבויות",
  addRequest: "בקשת סיוע",
  contactPhone: "טלפון",
  heart: "לב",
  home: "בית",
  location: "מיקום",
  sun: "שמש",
  chat: "צ׳אט",
  calendar: "יומן",
  star: "כוכב",
  building: "בניין",
  gift: "מתנה",
  plant: "צמח",
  help: "עזרה",
  medical: "רפואה",
  users: "אנשים",
  charity: "צדקה",
};

const ICON_GLYPHS: Record<OpeningHubIconKey, IconSvgElement> = {
  handshake: Agreement01Icon,
  opportunities: UserGroupIcon,
  addRequest: HeartAddIcon,
  contactPhone: CallIcon,
  heart: FavouriteIcon,
  home: Home01Icon,
  location: MapsLocation01Icon,
  sun: Sun03Icon,
  chat: Message01Icon,
  calendar: Calendar03Icon,
  star: StarIcon,
  building: Building03Icon,
  gift: GiftIcon,
  plant: Plant01Icon,
  help: HelpCircleIcon,
  medical: FirstAidKitIcon,
  users: UserMultipleIcon,
  charity: CharityIcon,
};

export function isOpeningHubIconKey(value: unknown): value is OpeningHubIconKey {
  return (
    typeof value === "string" &&
    (OPENING_HUB_ICON_KEYS as readonly string[]).includes(value)
  );
}

export function isOpeningHubAccentColor(value: unknown): value is string {
  return typeof value === "string" && ACCENT_COLOR_RE.test(value.trim());
}

export function normalizeOpeningHubAccentColor(value: unknown): string {
  if (!isOpeningHubAccentColor(value)) return OPENING_HUB_DEFAULT_ACCENT_COLOR;
  return value.trim().toLowerCase();
}

export function normalizeInternalPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function isSafeInternalPath(path: string): boolean {
  const normalized = normalizeInternalPath(path);
  return (
    !!normalized &&
    !normalized.includes("://") &&
    !normalized.startsWith("//") &&
    SAFE_INTERNAL_PATH_RE.test(normalized)
  );
}

const createEmptyOption = (): OpeningHubOptionFormItem => ({
  id: crypto.randomUUID(),
  label: "",
  description: "",
  path: "",
  iconKey: "handshake",
  accentColor: OPENING_HUB_DEFAULT_ACCENT_COLOR,
  enabled: true,
});

type OpeningHubOptionsEditorProps = {
  options: OpeningHubOptionFormItem[];
  onChange: (options: OpeningHubOptionFormItem[]) => void;
};

const OpeningHubOptionsEditor: FC<OpeningHubOptionsEditorProps> = ({
  options,
  onChange,
}) => {
  const atCap = options.length >= MAX_OPENING_HUB_OPTIONS;
  const canDelete = options.length > 1;

  const patchOption = <K extends keyof OpeningHubOptionFormItem>(
    index: number,
    key: K,
    value: OpeningHubOptionFormItem[K],
  ) => {
    onChange(
      options.map((option, i) =>
        i === index ? { ...option, [key]: value } : option,
      ),
    );
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const next = [...options];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    onChange(next);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={700}>
        אפשרויות מסך הפתיחה
      </Typography>
      <Typography color="text.secondary" fontSize="0.85rem">
        סדרו, הפעילו או כבו, ועדכנו תווית, תיאור, נתיב, אייקון וצבע. עד{" "}
        {MAX_OPENING_HUB_OPTIONS} אפשרויות.
      </Typography>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="opening-hub-options">
          {(provided) => (
            <Stack
              spacing={1.5}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {options.map((option, index) => (
                <Draggable
                  key={option.id}
                  draggableId={option.id}
                  index={index}
                >
                  {(dragProvided, snapshot) => (
                    <Paper
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: snapshot.isDragging
                          ? "action.hover"
                          : "background.paper",
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={1}
                        >
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <Box
                              {...dragProvided.dragHandleProps}
                              sx={{
                                display: "flex",
                                cursor: "grab",
                                color: "text.secondary",
                                touchAction: "none",
                              }}
                              aria-label="גרירת אפשרות"
                            >
                              <DragIndicatorIcon />
                            </Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={option.enabled}
                                  onChange={(_, checked) =>
                                    patchOption(index, "enabled", checked)
                                  }
                                />
                              }
                              label="מוצג"
                            />
                          </Stack>
                          <IconButton
                            aria-label="מחיקת אפשרות"
                            color="error"
                            size="small"
                            disabled={!canDelete}
                            onClick={() =>
                              onChange(options.filter((_, i) => i !== index))
                            }
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>

                        <TextField
                          label="כותרת"
                          value={option.label}
                          onChange={(e) =>
                            patchOption(index, "label", e.target.value)
                          }
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="תיאור קצר"
                          value={option.description}
                          onChange={(e) =>
                            patchOption(index, "description", e.target.value)
                          }
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="נתיב באפליקציה"
                          value={option.path}
                          onChange={(e) =>
                            patchOption(index, "path", e.target.value)
                          }
                          fullWidth
                          size="small"
                          helperText="נתיב פנימי באפליקציה בלבד (למשל /summer-campaign)"
                        />
                        <FormControl fullWidth size="small">
                          <InputLabel id={`opening-hub-icon-${option.id}`}>
                            אייקון
                          </InputLabel>
                          <Select
                            labelId={`opening-hub-icon-${option.id}`}
                            label="אייקון"
                            value={option.iconKey}
                            onChange={(e) =>
                              patchOption(
                                index,
                                "iconKey",
                                e.target.value as OpeningHubIconKey,
                              )
                            }
                          >
                            {OPENING_HUB_ICON_KEYS.map((key) => (
                              <MenuItem key={key} value={key}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  gap={1}
                                >
                                  <HugeiconsIcon
                                    icon={ICON_GLYPHS[key]}
                                    size={18}
                                    strokeWidth={1.8}
                                    color={option.accentColor}
                                  />
                                  {ICON_LABELS[key]}
                                </Stack>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <Stack spacing={0.75}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            צבע הכפתור
                          </Typography>
                          <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={1}
                            alignItems="center"
                          >
                            {OPENING_HUB_ACCENT_PRESETS.map((color) => {
                              const selected =
                                option.accentColor.toLowerCase() === color;
                              return (
                                <Box
                                  key={color}
                                  component="button"
                                  type="button"
                                  aria-label={`בחירת צבע ${color}`}
                                  aria-pressed={selected}
                                  onClick={() =>
                                    patchOption(index, "accentColor", color)
                                  }
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    bgcolor: color,
                                    border: selected
                                      ? "2px solid"
                                      : "2px solid transparent",
                                    borderColor: selected
                                      ? "text.primary"
                                      : "transparent",
                                    outline: selected
                                      ? "2px solid"
                                      : "1px solid",
                                    outlineColor: selected
                                      ? color
                                      : "rgba(0,0,0,0.12)",
                                    outlineOffset: selected ? 2 : 0,
                                    cursor: "pointer",
                                    p: 0,
                                    transition: "transform 0.15s ease",
                                    "&:hover": {
                                      transform: "scale(1.08)",
                                    },
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        </Stack>
                      </Stack>
                    </Paper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>

      <Button
        variant="outlined"
        disabled={atCap}
        onClick={() => onChange([...options, createEmptyOption()])}
        sx={{ alignSelf: "flex-start" }}
      >
        הוספת אפשרות
      </Button>
    </Stack>
  );
};

export default OpeningHubOptionsEditor;
